/**
 * CorrelationFormWizard.jsx
 * Formulario de Correlacao em Etapas Premium
 *
 * 4 Passos:
 * 1. Basico - Nome, Descricao
 * 2. Dispositivos - Dispositivo principal, Alvo (Device/POI), Raio
 * 3. Configuracoes - Tipo de alerta, Intrusivo, Habilitado
 * 4. Revisao
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Chip,
  Divider,
  TextField,
  Alert,
} from '@mui/material';
import {
  Link as LinkIcon,
  Settings as SettingsIcon,
  Devices as DevicesIcon,
  Place as PlaceIcon,
  CheckCircle as ReviewIcon,
} from '@mui/icons-material';

import { useTranslation } from '../../common/components/LocalizationProvider';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useEffectAsync } from '../../reactHelper';
import fetchOrThrow from '../../common/util/fetchOrThrow';

import PremiumWizard from '../../common/components/premium/PremiumWizard';
import PremiumInput from '../../common/components/premium/PremiumInput';
import { getWizardColors } from '../../common/components/premium/WizardStepTemplates';
import SearchableSelect from '../../common/components/SearchableSelect';
import EditAttributesAccordion from './EditAttributesAccordion';

// ============================================================================
// STEP 1: INFORMACOES BASICAS
// ============================================================================

const BasicInfoStep = ({ data, updateData, errors }) => {
  const t = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            label={`${t('sharedName')} *`}
            value={data.name || ''}
            onChange={(e) => updateData({ name: e.target.value })}
            error={!!errors?.name}
            helperText={errors?.name}
            autoFocus
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            label={t('sharedDescription')}
            value={data.description || ''}
            onChange={(e) => updateData({ description: e.target.value })}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// ============================================================================
// STEP 2: DISPOSITIVOS E ALVO
// ============================================================================

const DevicesStep = ({ data, updateData, errors, devices, pois }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  // Filtra dispositivos alvo (exclui o dispositivo principal)
  const targetDevices = useMemo(() =>
    devices.filter((d) => d.id !== data.deviceId),
  [devices, data.deviceId]);

  const handleTargetDeviceChange = (value) => {
    updateData({
      targetDeviceId: value || 0,
      targetPoiId: 0,
    });
  };

  const handleTargetPoiChange = (value) => {
    updateData({
      targetPoiId: value || 0,
      targetDeviceId: 0,
    });
  };

  const getTargetType = () => {
    if (data.targetPoiId > 0) return 'poi';
    if (data.targetDeviceId > 0) return 'device';
    return 'all';
  };

  // Custom render para POIs com cor e raio
  const renderPoiOption = (props, option) => {
    const { key, ...restProps } = props;
    if (option._isEmpty) {
      return (
        <Box component="li" {...restProps} key="empty" sx={{ color: colors.textMuted, fontStyle: 'italic', py: 1, px: 2 }}>
          {option.name}
        </Box>
      );
    }
    return (
      <Box
        component="li"
        {...restProps}
        key={option.id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1,
          px: 2,
          '&:hover': { backgroundColor: `${colors.accentSubtle} !important` },
          '&[aria-selected="true"]': { backgroundColor: `${colors.accentHover} !important` },
        }}
      >
        <PlaceIcon fontSize="small" sx={{ color: option.color || '#3B82F6' }} />
        <Typography variant="body2" sx={{ flex: 1 }}>{option.name}</Typography>
        {option.radius && (
          <Typography variant="caption" sx={{ color: colors.textMuted }}>
            ({option.radius}m)
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <SearchableSelect
            options={devices}
            value={data.deviceId}
            onChange={(value) => updateData({ deviceId: value })}
            label={t('correlationDevice')}
            required
            error={!!errors?.deviceId}
            helperText={errors?.deviceId}
            startAdornment={<DevicesIcon fontSize="small" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            type="number"
            label={`${t('correlationRadius')} *`}
            value={data.radiusMeters || 100}
            onChange={(e) => updateData({ radiusMeters: Number(e.target.value) })}
            error={!!errors?.radiusMeters}
            helperText={errors?.radiusMeters}
            inputProps={{ min: 1, step: 10 }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 1 }}>
        <Chip
          label={t('correlationTargetDevice')}
          icon={getTargetType() === 'poi' ? <PlaceIcon /> : <DevicesIcon />}
          size="small"
          sx={{
            backgroundColor: getTargetType() !== 'all' ? colors.accentSubtle : colors.surfaceHover,
            color: getTargetType() !== 'all' ? colors.accent : colors.textSecondary,
            border: `1px solid ${getTargetType() !== 'all' ? colors.accent : colors.borderSubtle}40`,
          }}
        />
      </Divider>

      <Grid container spacing={3} alignItems="center">
        <Grid size={{ xs: 12, sm: 5 }}>
          <SearchableSelect
            options={targetDevices}
            value={data.targetDeviceId || null}
            onChange={handleTargetDeviceChange}
            label={t('correlationTargetDevice')}
            disabled={data.targetPoiId > 0}
            emptyOption={{ value: null, label: t('sharedAll') }}
            startAdornment={<DevicesIcon fontSize="small" />}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="center">
            <Typography variant="caption" sx={{ color: colors.textMuted }}>
              {t('sharedOr')}
            </Typography>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, sm: 5 }}>
          <SearchableSelect
            options={pois}
            value={data.targetPoiId || null}
            onChange={handleTargetPoiChange}
            label={t('correlationTargetPoi')}
            disabled={data.targetDeviceId > 0}
            emptyOption={{ value: null, label: t('sharedNone') }}
            renderOption={renderPoiOption}
            startAdornment={<PlaceIcon fontSize="small" />}
          />
        </Grid>
      </Grid>

      <Box
        sx={{
          p: 2,
          borderRadius: tokens?.radius?.md || '8px',
          backgroundColor: colors.surfaceHover,
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '13px' }}>
          {t('correlationTargetHelp') || 'Selecione um dispositivo alvo especifico, um POI, ou deixe em branco para monitorar proximidade com todos os dispositivos.'}
        </Typography>
      </Box>
    </Box>
  );
};

// ============================================================================
// STEP 3: CONFIGURACOES
// ============================================================================

const ConfigStep = ({ data, updateData }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const isMandatory = data.alertType === 'mandatory';
  const isScheduled = data.scheduleType === 'scheduled';

  const daysOfWeek = [
    { value: 1, label: t('calendarMonday') || 'Seg' },
    { value: 2, label: t('calendarTuesday') || 'Ter' },
    { value: 3, label: t('calendarWednesday') || 'Qua' },
    { value: 4, label: t('calendarThursday') || 'Qui' },
    { value: 5, label: t('calendarFriday') || 'Sex' },
    { value: 6, label: t('calendarSaturday') || 'Sab' },
    { value: 7, label: t('calendarSunday') || 'Dom' },
  ];

  const handleDayToggle = (dayValue) => {
    const currentDays = data.scheduleDays ? data.scheduleDays.split(',').map(Number) : [];
    const newDays = currentDays.includes(dayValue)
      ? currentDays.filter((d) => d !== dayValue)
      : [...currentDays, dayValue].sort((a, b) => a - b);
    updateData({ scheduleDays: newDays.join(',') });
  };

  const selectedDays = data.scheduleDays ? data.scheduleDays.split(',').map(Number) : [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small" className="premium-input">
            <InputLabel>{t('correlationAlertType')}</InputLabel>
            <Select
              label={t('correlationAlertType')}
              value={data.alertType || 'approach'}
              onChange={(e) => {
                const newAlertType = e.target.value;
                const updates = { alertType: newAlertType };
                if (newAlertType === 'mandatory') {
                  updates.scheduleType = 'scheduled';
                }
                updateData(updates);
              }}
            >
              <MenuItem value="approach">{t('correlationAlertTypeApproach')}</MenuItem>
              <MenuItem value="separation">{t('correlationAlertTypeSeparation')}</MenuItem>
              <MenuItem value="both">{t('correlationAlertTypeBoth')}</MenuItem>
              <MenuItem value="mandatory">{t('correlationAlertTypeMandatory') || 'Presenca Obrigatoria'}</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {isMandatory && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small" className="premium-input">
              <InputLabel>{t('correlationScheduleType') || 'Tipo de Agendamento'}</InputLabel>
              <Select
                label={t('correlationScheduleType') || 'Tipo de Agendamento'}
                value={data.scheduleType || 'always'}
                onChange={(e) => updateData({ scheduleType: e.target.value })}
              >
                <MenuItem value="always">{t('correlationScheduleAlways') || 'Sempre (24/7)'}</MenuItem>
                <MenuItem value="scheduled">{t('correlationScheduleTimed') || 'Horario Especifico'}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>

      {isMandatory && (
        <Alert severity="info" sx={{ backgroundColor: colors.accentSubtle, border: `1px solid ${colors.accent}40` }}>
          <Typography variant="body2">
            {t('correlationMandatoryHint') || 'Presenca Obrigatoria: Alerta quando o dispositivo SAI da area durante o horario configurado. Ideal para monitoramento de tornozeleiras com recolhimento domiciliar.'}
          </Typography>
        </Alert>
      )}

      {isMandatory && isScheduled && (
        <Box
          sx={{
            p: 2,
            borderRadius: tokens?.radius?.md || '8px',
            backgroundColor: colors.surfaceHover,
            border: `1px solid ${colors.borderSubtle}`,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: colors.textPrimary,
              fontWeight: 600,
              textTransform: 'uppercase',
              fontSize: '11px',
              letterSpacing: '0.05em',
              mb: 2,
            }}
          >
            {t('correlationScheduleConfig') || 'Configuracao de Horario'}
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="time"
                label={t('correlationScheduleStart') || 'Horario Inicio'}
                value={data.scheduleStart || ''}
                onChange={(e) => updateData({ scheduleStart: e.target.value })}
                InputLabelProps={{ shrink: true }}
                className="premium-input"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="time"
                label={t('correlationScheduleEnd') || 'Horario Fim'}
                value={data.scheduleEnd || ''}
                onChange={(e) => updateData({ scheduleEnd: e.target.value })}
                InputLabelProps={{ shrink: true }}
                className="premium-input"
              />
            </Grid>
          </Grid>

          <Typography
            variant="subtitle2"
            sx={{
              color: colors.textPrimary,
              fontWeight: 600,
              textTransform: 'uppercase',
              fontSize: '11px',
              letterSpacing: '0.05em',
              mt: 2,
              mb: 1,
            }}
          >
            {t('correlationScheduleDays') || 'Dias da Semana'}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {daysOfWeek.map((day) => (
              <Chip
                key={day.value}
                label={day.label}
                onClick={() => handleDayToggle(day.value)}
                color={selectedDays.includes(day.value) ? 'primary' : 'default'}
                variant={selectedDays.includes(day.value) ? 'filled' : 'outlined'}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: colors.accentSubtle },
                }}
              />
            ))}
          </Box>

          <Typography variant="caption" sx={{ color: colors.textMuted, mt: 1, display: 'block' }}>
            {t('correlationScheduleDaysHint') || 'Deixe vazio para todos os dias. Suporta horarios noturnos (ex: 20:00 a 07:00).'}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: 2,
          borderRadius: tokens?.radius?.md || '8px',
          backgroundColor: colors.surfaceHover,
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={data.intrusive || false}
              onChange={(e) => updateData({ intrusive: e.target.checked })}
              sx={{
                color: colors.textSecondary,
                '&.Mui-checked': { color: colors.accent },
              }}
            />
          }
          label={
            <Box>
              <Typography sx={{ color: colors.textPrimary, fontSize: '14px' }}>
                {t('correlationIntrusive')}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                {t('correlationIntrusiveHint')}
              </Typography>
            </Box>
          }
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={data.enabled !== false}
              onChange={(e) => updateData({ enabled: e.target.checked })}
              sx={{
                color: colors.textSecondary,
                '&.Mui-checked': { color: colors.accent },
              }}
            />
          }
          label={
            <Typography sx={{ color: colors.textPrimary, fontSize: '14px' }}>
              {t('correlationEnabled')}
            </Typography>
          }
        />
      </Box>

      {/* Atributos */}
      <Box>
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.textPrimary,
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.05em',
            mb: 2,
          }}
        >
          {t('sharedAttributes')}
        </Typography>
        <EditAttributesAccordion
          attributes={data.attributes}
          setAttributes={(attributes) => updateData({ attributes })}
          definitions={{}}
          hideAccordion
        />
      </Box>
    </Box>
  );
};

// ============================================================================
// STEP 4: REVISAO
// ============================================================================

const ReviewStep = ({ data, devices, pois }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const InfoRow = ({ label, value, color }) => {
    if (!value && value !== 0 && value !== false) return null;
    return (
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '11px' }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: color || colors.textPrimary, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}
        >
          {typeof value === 'boolean' ? (value ? t('sharedYes') : t('sharedNo')) : value}
        </Typography>
      </Box>
    );
  };

  const device = devices.find((d) => d.id === data.deviceId);
  const targetDevice = devices.find((d) => d.id === data.targetDeviceId);
  const targetPoi = pois.find((p) => p.id === data.targetPoiId);

  const alertTypeLabels = {
    approach: t('correlationAlertTypeApproach'),
    separation: t('correlationAlertTypeSeparation'),
    both: t('correlationAlertTypeBoth'),
    mandatory: t('correlationAlertTypeMandatory') || 'Presenca Obrigatoria',
  };

  const scheduleTypeLabels = {
    always: t('correlationScheduleAlways') || 'Sempre (24/7)',
    scheduled: t('correlationScheduleTimed') || 'Horario Especifico',
  };

  const formatScheduleDays = (days) => {
    if (!days) return t('sharedAll') || 'Todos';
    const dayNames = {
      1: 'Seg', 2: 'Ter', 3: 'Qua', 4: 'Qui', 5: 'Sex', 6: 'Sab', 7: 'Dom',
    };
    return days.split(',').map((d) => dayNames[d] || d).join(', ');
  };

  const getTargetLabel = () => {
    if (targetPoi) return `POI: ${targetPoi.name}`;
    if (targetDevice) return targetDevice.name;
    return t('sharedAll');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          backgroundColor: colors.accentSubtle,
          borderRadius: tokens?.radius?.md || '8px',
          border: `1px solid ${colors.accent}30`,
        }}
      >
        <ReviewIcon sx={{ color: colors.accent }} />
        <Typography sx={{ color: colors.textPrimary }}>
          {t('wizardReviewConfirm')}
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: colors.surfaceElevated,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${colors.borderSubtle}`,
          p: 2.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.textPrimary,
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.05em',
            mb: 2,
          }}
        >
          {t('sharedRequired')}
        </Typography>
        <InfoRow label={t('sharedName')} value={data.name} />
        <InfoRow label={t('sharedDescription')} value={data.description || '-'} />
        <InfoRow label={t('correlationDevice')} value={device?.name || '-'} />
        <InfoRow label={t('correlationRadius')} value={`${data.radiusMeters || 100}m`} />
        <InfoRow label={t('correlationTargetDevice')} value={getTargetLabel()} />
      </Box>

      <Box
        sx={{
          backgroundColor: colors.surfaceElevated,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${colors.borderSubtle}`,
          p: 2.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.textPrimary,
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.05em',
            mb: 2,
          }}
        >
          {t('sharedPreferences')}
        </Typography>
        <InfoRow label={t('correlationAlertType')} value={alertTypeLabels[data.alertType] || alertTypeLabels.approach} />
        {data.alertType === 'mandatory' && (
          <>
            <InfoRow
              label={t('correlationScheduleType') || 'Agendamento'}
              value={scheduleTypeLabels[data.scheduleType] || scheduleTypeLabels.always}
            />
            {data.scheduleType === 'scheduled' && (
              <>
                <InfoRow
                  label={t('correlationScheduleTime') || 'Horario'}
                  value={`${data.scheduleStart || '00:00'} - ${data.scheduleEnd || '23:59'}`}
                />
                <InfoRow
                  label={t('correlationScheduleDays') || 'Dias'}
                  value={formatScheduleDays(data.scheduleDays)}
                />
              </>
            )}
          </>
        )}
        <InfoRow label={t('correlationIntrusive')} value={data.intrusive} />
        <InfoRow
          label={t('correlationEnabled')}
          value={data.enabled !== false}
          color={data.enabled !== false ? colors.success : colors.error}
        />
      </Box>
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const CorrelationFormWizard = ({ correlation, onSave, onCancel }) => {
  const t = useTranslation();
  const navigate = useNavigate();

  const [devices, setDevices] = useState([]);
  const [pois, setPois] = useState([]);

  // Fetch devices and POIs
  useEffectAsync(async () => {
    try {
      const [devicesRes, poisRes] = await Promise.all([
        fetchOrThrow('/api/devices'),
        fetchOrThrow('/api/pois'),
      ]);
      setDevices(await devicesRes.json());
      setPois(await poisRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, []);

  const steps = [
    {
      label: t('wizardStepBasic'),
      title: t('sharedRequired'),
      description: t('wizardCorrelationBasicDescription') || 'Identificacao da correlacao',
      icon: <LinkIcon sx={{ fontSize: 18 }} />,
      component: BasicInfoStep,
      validate: (data) => {
        const errors = {};
        if (!data.name?.trim()) errors.name = t('sharedRequired');
        return Object.keys(errors).length ? errors : true;
      },
      getSummary: (data) => [
        { label: t('sharedName'), value: data.name },
      ],
    },
    {
      label: t('deviceTitle') || 'Dispositivos',
      title: t('correlationDevice') || 'Dispositivos',
      description: t('wizardCorrelationDevicesDescription') || 'Dispositivo principal e alvo',
      icon: <DevicesIcon sx={{ fontSize: 18 }} />,
      component: (props) => <DevicesStep {...props} devices={devices} pois={pois} />,
      validate: (data) => {
        const errors = {};
        if (!data.deviceId) errors.deviceId = t('sharedRequired');
        if (!data.radiusMeters || data.radiusMeters <= 0) errors.radiusMeters = t('sharedRequired');
        return Object.keys(errors).length ? errors : true;
      },
    },
    {
      label: t('sharedPreferences') || 'Config',
      title: t('sharedPreferences') || 'Configuracoes',
      description: t('wizardCorrelationConfigDescription') || 'Tipo de alerta e opcoes',
      icon: <SettingsIcon sx={{ fontSize: 18 }} />,
      component: ConfigStep,
      optional: true,
    },
    {
      label: t('wizardStepReview'),
      title: t('wizardReviewTitle'),
      description: t('wizardReviewDescription'),
      icon: <ReviewIcon sx={{ fontSize: 18 }} />,
      component: (props) => <ReviewStep {...props} devices={devices} pois={pois} />,
    },
  ];

  const handleComplete = async (data) => {
    try {
      const url = correlation?.id ? `/api/correlations/${correlation.id}` : '/api/correlations';
      const method = correlation?.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const saved = await response.json();
        onSave?.(saved);
        navigate('/settings/correlations');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t('errorGeneric'));
      }
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    navigate('/settings/correlations');
  };

  const initialData = correlation || {
    name: '',
    description: '',
    deviceId: null,
    targetDeviceId: 0,
    targetPoiId: 0,
    radiusMeters: 100,
    alertType: 'approach',
    intrusive: false,
    enabled: true,
    scheduleType: 'always',
    scheduleStart: '',
    scheduleEnd: '',
    scheduleDays: '',
    attributes: {},
  };

  return (
    <PremiumWizard
      steps={steps}
      initialData={initialData}
      onComplete={handleComplete}
      onCancel={handleCancel}
      storageKey={correlation?.id ? null : 'correlation-wizard-draft'}
      completeButtonText={correlation?.id ? t('sharedSave') : t('sharedAdd')}
      nextButtonText={t('wizardNext')}
      backButtonText={t('sharedBack')}
      cancelButtonText={t('sharedCancel')}
      skipButtonText={t('wizardSkip')}
      showSummary
    />
  );
};

export default CorrelationFormWizard;
